import * as React from "react";
import { AutoCompleteInput, Input } from "uxp/components";
import { findAvailableFields, IUIPropertyDefinition } from "../../../common/common";
import { CustomColorPicker, Property, PropertyContainer, PropertyContainerTitle, PropertyGroup, PropertyTitle } from "../../../common/ui-props/common";
import IconEditor from "../../../common/icon-selector/IconEditor";
import { Conditional } from "../../../common/ConditionalComponent";
import { DataSourcePropertyEditor } from "../../../common/ui-props/DataSourceProperty";
import { bindContainerId } from "../../../../util";
import ActionEditorLink, { IAction } from "../../../common/action-editor/ActionEditor";
import { IDataSource, IDataSourceBinding } from "../../../common/data-source-picker/datasource";
import jp from 'jsonpath'

export interface IBoxProps {
    label: string,
    labelColor: string,
    icon: string,
    iconColor: string,
    fieldName: string,
    defaultValue: any,
    backgroundColor: string,
    datasource: IDataSourceBinding,
    onClickAction?: IAction,
}

export interface IMultipleValueWidgetProps {
    boxProps: IBoxProps[]
}
export interface IMultipleValueCommonProps {
    onClickChart?: IAction
}

export function MultipleValueCommonProperty(widgetCommonProps: IMultipleValueCommonProps): IUIPropertyDefinition<IMultipleValueCommonProps> {

    return {
        defaults: widgetCommonProps,
        render: (props, uxpContext, data, containerId) => {
            let _props = props.uiProps
            return <>
                {
                    <PropertyContainer>
                        <PropertyGroup>
                            <Property>
                                <PropertyTitle>On click an item </PropertyTitle>
                                <ActionEditorLink
                                    uiProps={props.parentUIProps}
                                    designer={props.designer}
                                    uxpContext={uxpContext}
                                    action={_props?.onClickChart || null}
                                    onChange={s => {
                                        if (!_props?.onClickChart) _props.onClickChart = null
                                        jp.apply(_props, '$.onClickChart', () => (s))
                                        props.onChange(_props)
                                    }}
                                    contextData={props.contextData}
                                />
                            </Property>
                        </PropertyGroup>
                    </PropertyContainer>
                }
            </>
        }
    };
}

export type SelectablePart = 'icon' | 'label' | 'value' | 'all'

export let defaultBoxProps: IBoxProps = {
    label: 'Sample Text',
    labelColor: '#FFFFFF88',
    icon: 'angleUp',
    iconColor: '#FFFFFF88',
    fieldName: '',
    defaultValue: '537',
    backgroundColor: '000000',
    datasource: null,
};

export function MultipleValueWidgetProperty(widgetProps: IMultipleValueWidgetProps): IUIPropertyDefinition<IMultipleValueWidgetProps> {

    return {
        defaults: widgetProps,
        render: (props, uxpContext, data, containerId) => {
            let _props = props.uiProps
            let { selectedIndex, selectedPart } = data

            let sources: IDataSource[] = props?.parentUIProps?.datasources

            let selectedBoxProp = widgetProps?.boxProps[selectedIndex];
            if (!selectedBoxProp) selectedBoxProp = defaultBoxProps

            let source = selectedBoxProp?.datasource

            let possibleValues = {};
            findAvailableFields(source, sources, possibleValues);
            let pv = Object.keys(possibleValues);
            pv.sort();

            return <>
                {
                    <PropertyContainer>
                        <PropertyContainerTitle>Box Properties</PropertyContainerTitle>

                        <Conditional visible={selectedPart == 'all' || selectedPart == 'value'}>
                            <PropertyGroup>
                                <Property id={bindContainerId('field-name-' + selectedIndex, containerId)}>
                                    <PropertyTitle>Field Name</PropertyTitle>
                                    <AutoCompleteInput
                                        value={selectedBoxProp?.fieldName || ''}
                                        onChange={s => {
                                            jp.apply(_props, `$.boxProps[${selectedIndex}].fieldName`, () => (s))
                                            props.onChange(_props)
                                        }}
                                        className="widget-designer-input"
                                        options={pv}
                                    />
                                </Property>

                                <Property>
                                    <PropertyTitle>Default Value</PropertyTitle>
                                    <Input
                                        value={selectedBoxProp?.defaultValue || ''}
                                        onChange={s => {
                                            jp.apply(_props, `$.boxProps[${selectedIndex}].defaultValue`, () => (s))
                                            props.onChange(_props)
                                        }}
                                        className="widget-designer-input"
                                    />
                                </Property>
                            </PropertyGroup>
                        </Conditional>


                        <Conditional visible={selectedPart == 'all' || selectedPart == 'value'}>
                            <Property id={bindContainerId('field-data-source-' + selectedIndex, containerId)}>
                                <DataSourcePropertyEditor
                                    title={'Source'}
                                    binding={source}
                                    sources={sources}
                                    target={'dictionary'}
                                    onChange={s => {
                                        jp.apply(_props, `$.boxProps[${selectedIndex}].datasource`, () => (s))
                                        props.onChange(_props)
                                    }}
                                    designer={props.designer}
                                    onChangeAny={props.onChangeAny}
                                    context={props.uxpContext}
                                    contextData={props.contextData}
                                />
                            </Property>
                        </Conditional>


                        <Conditional visible={selectedPart == 'all'}>
                            <PropertyGroup>
                                <Property>
                                    <PropertyTitle>Background Color</PropertyTitle>
                                    <CustomColorPicker contextData={props.contextData}
                                        color={selectedBoxProp?.backgroundColor || 'white'}
                                        onChange={s => {
                                            jp.apply(_props, `$.boxProps[${selectedIndex}].backgroundColor`, () => (s))
                                            props.onChange(_props)
                                        }}
                                    />
                                </Property>
                            </PropertyGroup>
                        </Conditional>

                        <Conditional visible={selectedPart == 'all' || selectedPart == 'label'}>
                            <PropertyGroup>
                                <Property>
                                    <PropertyTitle>Label</PropertyTitle>
                                    <Input
                                        value={selectedBoxProp?.label || ''}
                                        onChange={s => {
                                            jp.apply(_props, `$.boxProps[${selectedIndex}].label`, () => (s))
                                            props.onChange(_props)
                                        }}
                                        className="widget-designer-input"
                                    />
                                </Property>

                                <Property>
                                    <PropertyTitle>Label Color</PropertyTitle>
                                    <CustomColorPicker contextData={props.contextData}
                                        color={selectedBoxProp?.labelColor || 'white'}
                                        onChange={s => {
                                            jp.apply(_props, `$.boxProps[${selectedIndex}].labelColor`, () => (s))
                                            props.onChange(_props)
                                        }}

                                    />
                                </Property>
                            </PropertyGroup>
                        </Conditional>

                        <Conditional visible={selectedPart == 'all' || selectedPart == 'icon'}>
                            <PropertyGroup>
                                <Property>
                                    <PropertyTitle>{'Icon'}</PropertyTitle>
                                    <IconEditor
                                        value={selectedBoxProp?.icon || ''}
                                        onChange={s => {
                                            jp.apply(_props, `$.boxProps[${selectedIndex}].icon`, () => (s))
                                            props.onChange(_props)
                                        }}
                                    />
                                </Property>

                                <Property>
                                    <PropertyTitle>Icon Color</PropertyTitle>
                                    <CustomColorPicker contextData={props.contextData}
                                        color={selectedBoxProp?.iconColor || 'white'}
                                        onChange={s => {
                                            jp.apply(_props, `$.boxProps[${selectedIndex}].iconColor`, () => (s))
                                            props.onChange(_props)
                                        }}
                                    />
                                </Property>
                                <Property>
                                    <PropertyTitle>Box Drilldown Action</PropertyTitle>
                                    <ActionEditorLink
                                        uiProps={props.parentUIProps}
                                        designer={props.designer}
                                        uxpContext={uxpContext}
                                        action={selectedBoxProp?.onClickAction}
                                        onChange={s => {
                                            console.log('ACTION', s);
                                            if (!_props.boxProps[selectedIndex].onClickAction) {
                                                _props.boxProps[selectedIndex].onClickAction = null;
                                            }
                                            jp.apply(_props, `$.boxProps[${selectedIndex}].onClickAction`, () => (s))
                                            props.onChange(_props)
                                        }}
                                        contextData={props.contextData}
                                    />
                                </Property>
                            </PropertyGroup>
                        </Conditional>
                    </PropertyContainer>
                }
            </>
        }
    };
}
